/**
 * Best-effort EEA/UK detection from the browser's IANA timezone. Client-only —
 * it relies on the visitor's own `Intl` timezone, so call it from the browser
 * (e.g. inside PostHogProvider's effect), not during server rendering where the
 * timezone would be the server's.
 *
 * Intentionally over-inclusive: treating a few non-EEA European visitors
 * (Switzerland, Turkey, the Balkans, etc.) as in-scope only makes analytics more
 * conservative — they get the cookieless path too — and never less compliant.
 * Under-detection is the only real risk, so we err wide and default to the safe
 * (cookieless) branch whenever the timezone is unavailable.
 */
export function isEeaOrUkRegion(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    // EEA + UK + a handful of non-EEA European zones — all fine to include.
    if (tz.startsWith("Europe/")) return true;
    // Iceland and the EU outermost regions sit under Atlantic/*.
    return [
      "Atlantic/Reykjavik", // Iceland (EEA)
      "Atlantic/Canary", // Spain
      "Atlantic/Madeira", // Portugal
      "Atlantic/Azores", // Portugal
      "Atlantic/Faroe", // Denmark
    ].includes(tz);
  } catch {
    return true; // no timezone → assume in-scope and stay cookieless.
  }
}
